import type {
  BusinessFeeNegotiation,
  BusinessFeeNegotiationFilter,
  BusinessFeeNegotiationLineRequest,
  BusinessFeeNegotiationList,
  BusinessFeeScheduleRow,
} from '@afriex/core/domain/types';

import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminPaginatedEnvelope,
  AfriexQueryParams,
  AfriexRatePromo,
  AfriexWalletType,
  BusinessFee,
  BusinessPoolAccount,
  SupportedAssets,
} from '@/types';
import type {
  AfriexBusinessUser,
  AfriexBusinessUserMeta,
  AfriexMerchant,
} from '@/types/AdminBusinessUser';
import type {
  AfriexBusiness,
  BusinessAllowedFeature,
  BusinessAPIKeyResponse,
  BusinessMetaFlags,
} from '@/types/AfriexBusiness';
import type {BusinessPaymentMethod} from '@/types/AfriexBusinessPaymentMethod';
import type {BusinessKYBDocumentType} from '@/types/BusinessKYBDocumentType';
import type {BusinessKYCDocumentType} from '@/types/BusinessKYCDocumentType';
import type {
  AfriexAssetAmount,
  MarketPlaceAsk,
  MarketPlaceBid,
  MarketPlaceChannelsByCurrency,
  MarketPlaceChannelSupport,
  MarketPlaceTrade,
  MarketPlaceTradeList,
  MarketPlaceTradeQuery,
} from '@/types/MarketPlace';
import type {
  AfriexSecurityNote,
  AfriexSecurityNotesQueryParams,
} from '@/types/Notes';
import {parseBlobErrorBody} from '@/utils/downloadFile';

type KYCUpdatePayload = {
  [key in BusinessKYCDocumentType]?: string;
};

/** What the fee-schedule endpoint returns: the priced rows. */
export type BusinessFeeSchedule = {
  rows: BusinessFeeScheduleRow[];
};

export type FeeNegotiationQuery = {
  status?: BusinessFeeNegotiationFilter;
  merchantId?: string;
  page?: number;
  limit?: number;
};

export type FeeNegotiationAnswer = {
  action: 'accept' | 'reject' | 'counter' | 'apply';
  lines?: BusinessFeeNegotiationLineRequest[];
  /** The counter's note to the merchant. */
  note?: string;
  /** The decline reason, optional. */
  rejectionNote?: string;
};

type KYBUpdatePayload = {
  [key in BusinessKYBDocumentType]?: string;
};

export const merchantService = {
  getBusinessUsers: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexMerchant[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/business/users?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexMerchant[]>>(url);
    return response.data;
  },
  getBusinessUser: async (
    userId: string,
  ): Promise<{person: AfriexBusinessUser} | null> => {
    const response = await axios.get<{person: AfriexBusinessUser} | null>(
      `/v2/admin/business/users/${userId}`,
    );
    return response.data;
  },
  getBusinessDetails: async (
    businessId: string,
    signal?: AbortSignal,
  ): Promise<{business: AfriexBusiness; person: AfriexBusinessUser}> => {
    const response = await axios.get<{
      business: AfriexBusiness;
      person: AfriexBusinessUser;
    }>(`/v2/admin/business/details/${businessId}`, {signal});
    return response.data;
  },
  /**
   * One page of the negotiation queue. `merchantId` takes a merchant reference
   * (`afx12`) or a name; the server resolves either. `counts` are the tab
   * totals, ignoring paging.
   */
  getFeeNegotiations: async (
    filters: FeeNegotiationQuery = {},
    signal?: AbortSignal,
  ): Promise<BusinessFeeNegotiationList> => {
    // Only send what the reader picked: an absent status is the server's
    // default queue, which is not the same as `status=`.
    const params: Record<string, string | number> = {};
    if (filters.status) {
      params.status = filters.status;
    }
    if (filters.merchantId) {
      params.merchantId = filters.merchantId;
    }
    if (filters.page) {
      params.page = filters.page;
    }
    if (filters.limit) {
      params.limit = filters.limit;
    }

    const response = await axios.get<BusinessFeeNegotiationList>(
      '/v2/admin/business/fee-negotiations',
      {signal, params},
    );
    return response.data;
  },

  /** One business's latest negotiation, for its fee schedule tab. */
  getBusinessFeeNegotiation: async (
    businessId: string,
    signal?: AbortSignal,
  ): Promise<BusinessFeeNegotiation | null> => {
    const response = await axios.get<{data: BusinessFeeNegotiation | null}>(
      `/v2/admin/business/fee-negotiation/${businessId}`,
      {signal},
    );
    return response.data.data ?? null;
  },

  /**
   * Afriex's answer to an offer. `counter` sends the full set of lines;
   * `apply` retries writing the fee rules of an accepted negotiation.
   */
  respondToFeeNegotiation: async (
    businessId: string,
    negotiationId: string,
    payload: FeeNegotiationAnswer,
  ): Promise<BusinessFeeNegotiation> => {
    const response = await axios.put<{data: BusinessFeeNegotiation}>(
      `/v2/admin/business/fee-negotiation/${businessId}/${negotiationId}`,
      payload,
    );
    return response.data.data;
  },

  /**
   * The business's priced corridors, exactly as the business portal shows them,
   * with the ones it ticked marked. Read-only: an admin changes prices by
   * editing the fee rules on the Fees subtab, not here.
   */
  getBusinessFeeSchedule: async (
    businessId: string,
    signal?: AbortSignal,
  ): Promise<BusinessFeeSchedule> => {
    // The response interceptor passes the whole response through, so `.data` is
    // the `{data: ...}` envelope and `.data.data` is the schedule.
    const response = await axios.get<{data: BusinessFeeSchedule}>(
      `/v2/admin/business/fee-schedule/${businessId}`,
      {signal},
    );
    return response.data.data ?? {rows: []};
  },
  getBusinesses: async (
    filters: AfriexQueryParams,
  ): Promise<
    AdminPaginatedEnvelope<
      {business: AfriexBusiness; person: AfriexBusinessUser}[]
    >
  > => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/business?${urlParams}`;
    const response =
      await axios.get<
        AdminPaginatedEnvelope<
          {business: AfriexBusiness; person: AfriexBusinessUser}[]
        >
      >(url);
    const data = response.data?.data;
    const userListData = {...response.data, data};
    return userListData;
  },

  getBusinessWallet: async (
    businessId: string,
    currencyList: SupportedAssets[],
    signal?: AbortSignal,
  ): Promise<Partial<
    Record<AfriexWalletType, Partial<Record<SupportedAssets, number>>>
  > | null> => {
    const currencies = currencyList.join(',');
    const response = await axios.get<Partial<
      Record<AfriexWalletType, Partial<Record<SupportedAssets, number>>>
    > | null>(
      `/v2/admin/business/wallet/${businessId}?currencyList=${currencies}`,
      {signal},
    );
    return response.data;
  },

  updateWalletBalance: async (
    type: 'CREDIT' | 'DEBIT',
    amount: {
      currencyCode: string;
      value: number;
    },
    reason: string,
    businessId: string,
    walletTag?: string,
    note?: string,
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/wallet/${businessId}`,
      {
        type,
        amount,
        reason,
        ...(walletTag && {walletTag}),
        ...(note && {note}),
      },
    );
    return response.data;
  },

  updateUserKYC: async (
    businessId: string,
    userId: string,
    kyc: KYCUpdatePayload,
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/users/${userId}/kyc`,
      {
        businessId,
        kyc,
      },
    );
    return response.data;
  },

  updateBusinessKYB: async (
    businessId: string,
    kyb: KYBUpdatePayload,
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/kyb/${businessId}`,
      {
        kyb,
      },
    );
    return response.data;
  },

  requestBusinessUserKYCDocuments: async (
    businessId: string,
    userId: string,
    documents: BusinessKYCDocumentType[],
  ): Promise<void> => {
    const response = await axios.post<void>(
      `/v2/admin/business/users/${userId}/kyc/document-requests`,
      {businessId, documents},
    );
    return response.data;
  },

  requestBusinessKYBDocuments: async (
    businessId: string,
    documents: BusinessKYBDocumentType[],
  ): Promise<void> => {
    const response = await axios.post<void>(
      `/v2/admin/business/kyb/${businessId}/document-requests`,
      {documents},
    );
    return response.data;
  },

  updateUserFlag: async (
    businessId: string,
    userId: string,
    flag: Partial<AfriexBusinessUserMeta['flags']>,
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/users/${userId}/flag/`,
      {
        flag,
        businessId,
      },
    );
    return response.data;
  },

  updateBusinessFlag: async (
    businessId: string,
    flag: Partial<BusinessMetaFlags>,
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/flag/${businessId}`,
      {
        flag,
      },
    );
    return response.data;
  },

  updateBusinessPostFundingConfig: async (
    businessId: string,
    config: {
      isApprovedForPostFunding?: boolean;
      postFundingLimitUSD?: number;
      postFundingDailyInterestRatePercent?: number;
    },
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/post-funding-config/${businessId}`,
      config,
    );
    return response.data;
  },

  /**
   * Per-business Fee wallet overdraft limit (USD-equivalent). -1 means no
   * limit; when unset the global default applies. Only meaningful for a
   * business approved for fee-wallet settlement. Returns the updated business
   * so the caller can show the saved value without refetching.
   */
  updateBusinessFeeWalletOverdraftLimit: async (
    businessId: string,
    feeWalletOverdraftLimitUSD: number,
  ): Promise<AfriexBusiness> => {
    const response = await axios.patch<AfriexBusiness>(
      `/v2/admin/business/fee-wallet-overdraft-limit/${businessId}`,
      {feeWalletOverdraftLimitUSD},
    );
    return response.data;
  },

  // Team members of a business — powers the ownership-transfer picker.
  getBusinessMembers: async (
    businessId: string,
  ): Promise<AfriexBusinessUser[]> => {
    const response = await axios.get<
      AdminPaginatedEnvelope<AfriexBusinessUser[]>
    >(`/v2/admin/business/members/${businessId}`);
    return response.data?.data ?? [];
  },

  // A business's API keys, masked (the secret is never stored).
  getBusinessAPIKeys: async (
    businessId: string,
  ): Promise<BusinessAPIKeyResponse[]> => {
    const response = await axios.get<
      AdminPaginatedEnvelope<BusinessAPIKeyResponse[]>
    >(`/v2/admin/business/${businessId}/api-keys`);
    return response.data?.data ?? [];
  },

  // Ownership transfer / recovery: reassign the organization owner to another
  // active member (used when an owner leaves or is suspended).
  transferBusinessOwner: async (
    businessId: string,
    newOwnerUserId: string,
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/transfer-owner/${businessId}`,
      {newOwnerUserId},
    );
    return response.data;
  },

  // Duplicate consolidation: request that an existing member of a (usually
  // duplicate) source org join a confirmed target org. Creates an
  // owner-approved invite — does not move anyone directly. The target org's
  // owner must approve + assign a role, and the member must set a new
  // password to actually join. Used to bring pre-#5256 duplicate signups into
  // the main organization.
  requestMemberMove: async (
    userId: string,
    targetBusinessId: string,
  ): Promise<void> => {
    const response = await axios.post<void>(
      `/v2/admin/business/request-member-move`,
      {userId, targetBusinessId},
    );
    return response.data;
  },

  // Retire a consolidated-duplicate org after its members have been moved out.
  // Deactivates it AND hides it from the admin business list + search (a plain
  // deactivate keeps it visible). Server refuses if the org still has members.
  retireConsolidatedDuplicateOrg: async (businessId: string): Promise<void> => {
    const response = await axios.post<void>(
      `/v2/admin/business/retire/${businessId}`,
    );
    return response.data;
  },

  updateBusinessAllowedFeature: async (
    businessId: string,
    allowedFeatures: BusinessAllowedFeature[],
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/features/${businessId}`,
      {
        features: allowedFeatures,
      },
    );
    return response.data;
  },

  searchBusinessAutoComplete: async (
    searchTerm: string,
  ): Promise<
    AdminPaginatedEnvelope<
      {business: AfriexBusiness; person: AfriexBusinessUser}[]
    >
  > => {
    const term = searchTerm;
    const searchParams = toQueryString({
      searchTerm: term?.trim()?.toString(),
    });
    const url = `/v2/admin/business/autocomplete?${searchParams}`;
    const response =
      await axios.get<
        AdminPaginatedEnvelope<
          {business: AfriexBusiness; person: AfriexBusinessUser}[]
        >
      >(url);

    return response?.data;
  },

  searchUserAutoComplete: async (
    searchTerm: string,
  ): Promise<AfriexMerchant[]> => {
    let term = searchTerm;

    if (term.includes('@')) {
      term = term.replace(/@/g, '');
    }

    const searchParams = toQueryString({
      searchTerm: term?.trim()?.toString(),
    });
    const url = `/v2/admin/business/users/autocomplete?${searchParams}`;
    const response = await axios.get<AfriexMerchant[]>(url);

    return response.data || [];
  },

  getBusinessFees: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<BusinessFee[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/business/fees?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<BusinessFee[]>>(url);
    const data = response.data?.data;
    const listData = {...response.data, data};
    return listData;
  },

  getBusinessRatePromos: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexRatePromo[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/business/promos?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexRatePromo[]>>(url);
    const data = response.data?.data;
    const listData = {...response.data, data};
    return listData;
  },
  createBusinessFee: async (businessFee: BusinessFee): Promise<void> => {
    const response = await axios.post<void>(
      `/v2/admin/business/fees`,
      businessFee,
    );
    return response.data;
  },
  updateBusinessFee: async (
    feeId: string,
    feeUupdate: Partial<BusinessFee>,
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/fees/${feeId}`,
      feeUupdate,
    );
    return response.data;
  },
  getBusinessPoolAccounts: async (
    filters: AfriexQueryParams,
  ): Promise<BusinessPoolAccount[]> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/business/pool-accounts?${urlParams}`;
    const response = await axios.get<BusinessPoolAccount[]>(url);
    const data = response.data || [];
    return data;
  },
  createBusinessPoolAccount: async (
    businessPoolAccount: BusinessPoolAccount,
  ): Promise<void> => {
    const response = await axios.post<void>(
      `/v2/admin/business/pool-accounts`,
      businessPoolAccount,
    );
    return response.data;
  },
  updateBusinessPoolAccount: async (
    poolAccountId: string,
    poolAccountUupdate: Partial<BusinessPoolAccount>,
  ): Promise<void> => {
    const response = await axios.patch<void>(
      `/v2/admin/business/pool-accounts/${poolAccountId}`,
      poolAccountUupdate,
    );
    return response.data;
  },

  getBusinessSecurityNotes: async (
    id: string,
    filters: AfriexSecurityNotesQueryParams,
  ): Promise<{data: AfriexSecurityNote[]; page: number; limit: number}> => {
    const urlParams = toQueryString({page: 0, limit: 6, ...filters});
    const response = await axios.get<{
      data: AfriexSecurityNote[];
      page: number;
      limit: number;
    }>(`/v2/admin/business/security-notes/${id}?${urlParams}`);
    return response.data;
  },

  createMarketPlaceAsk: async (data: {
    volume: AfriexAssetAmount;
    quote: AfriexAssetAmount;
    minBidVolume?: string;
    maxAllowedOfferPrice?: string;
    expiresAt?: Date | string;
  }): Promise<MarketPlaceAsk> => {
    const response = await axios.post<MarketPlaceAsk>(
      `/v2/admin/market-place/asks`,
      data,
    );
    return response.data;
  },

  // The volume currencies a new ask may use, each with the rails it supports.
  // Derived server-side from the currency's configured processors and the pool
  // accounts ops has provisioned, so the ask form never offers a currency the
  // marketplace cannot pay out on.
  getMarketPlaceChannels: async (): Promise<MarketPlaceChannelsByCurrency> => {
    const response = await axios.get<{data: MarketPlaceChannelSupport}>(
      `/v2/admin/market-place/channels`,
    );
    return response.data.data?.channels ?? {};
  },

  getMarketPlaceAsks: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<MarketPlaceAsk[]>> => {
    const params = {page: 0, limit: 10, ...filters};
    const urlParams = toQueryString(params);
    const url = `/v2/admin/market-place/asks?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<MarketPlaceAsk[]>>(url);
    return {
      data: response.data.data || [],
      page: params.page,
      limit: params.limit,
      total: response.data.total || 0,
    };
  },

  updateMarketPlaceAsk: async (
    id: string | number,
    data: {
      volume?: AfriexAssetAmount;
      quote?: AfriexAssetAmount;
      minBidVolume?: string;
      maxAllowedOfferPrice?: string;
      expiresAt?: Date | string;
    },
  ): Promise<MarketPlaceAsk> => {
    const response = await axios.patch<MarketPlaceAsk>(
      `/v2/admin/market-place/asks/${id}`,
      data,
    );
    return response.data;
  },

  setMarketPlaceAskStatus: async (
    id: string | number,
    isDeactivated: boolean,
  ): Promise<MarketPlaceAsk> => {
    const response = await axios.patch<MarketPlaceAsk>(
      `/v2/admin/market-place/asks/${id}/status`,
      {isDeactivated},
    );
    return response.data;
  },

  deleteMarketPlaceAsk: async (id: string | number): Promise<void> => {
    await axios.delete(`/v2/admin/market-place/asks/${id}`);
  },

  getMarketPlaceBids: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<MarketPlaceBid[]>> => {
    const params = {page: 0, limit: 10, ...filters};
    const urlParams = toQueryString(params);
    const url = `/v2/admin/market-place/bids?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<MarketPlaceBid[]>>(url);
    return {
      data: response.data.data || [],
      page: params.page,
      limit: params.limit,
      total: response.data.total || 0,
    };
  },

  // Read-only trade history for the admin Trades view. `summary` is totals over
  // every matching trade, not just this page, and is applied through the same
  // filters as `data`.
  getMarketPlaceTrades: async (
    filters: MarketPlaceTradeQuery,
    signal?: AbortSignal,
  ): Promise<MarketPlaceTradeList> => {
    const url = `/v2/admin/market-place/trades?${toQueryString(filters)}`;
    const response = await axios.get<MarketPlaceTradeList>(url, {signal});
    return {
      data: response.data.data || [],
      total: response.data.total || 0,
      page: response.data.page || 0,
      summary: response.data.summary,
    };
  },

  getMarketPlaceTrade: async (
    id: string | number,
    signal?: AbortSignal,
  ): Promise<MarketPlaceTrade> => {
    const response = await axios.get<MarketPlaceTrade>(
      `/v2/admin/market-place/trades/${id}`,
      {signal},
    );
    return response.data;
  },

  // Every trade matching the filters as a CSV, streamed by the server. Only
  // the filters are sent: the server ignores paging for a download.
  downloadMarketPlaceTradesCsv: async (
    filters: MarketPlaceTradeQuery,
  ): Promise<Blob> => {
    const url = `/v2/admin/market-place/trades?${toQueryString({...filters, action: 'DOWNLOAD'})}`;
    try {
      const response = await axios.get<Blob>(url, {responseType: 'blob'});
      return response.data;
    } catch (error) {
      throw await parseBlobErrorBody(error);
    }
  },

  // Approve a business-posted offer (PENDING_ADMIN_APPROVAL -> OPEN). Business
  // offers carry a price the business set itself, so they are not matchable
  // until an admin accepts the terms.
  approveMarketPlaceOffer: async (id: string | number): Promise<void> => {
    await axios.patch(`/v2/admin/market-place/asks/${id}/approve`);
  },

  approveMarketPlaceBid: async (id: string | number): Promise<void> => {
    await axios.patch(`/v2/admin/market-place/bids/${id}/approve`);
  },

  rejectMarketPlaceBid: async (id: string | number): Promise<void> => {
    await axios.patch(`/v2/admin/market-place/bids/${id}/reject`);
  },

  // Read-only: all virtual accounts linked to a business (any customer,
  // currency, provider, or status). Backed by the admin endpoint
  // `GET /v2/admin/business/:businessId/virtual-accounts`.
  getBusinessVirtualAccounts: async (
    businessId: string,
    signal?: AbortSignal,
  ): Promise<BusinessPaymentMethod[]> => {
    // Server paginates now; ask for the max page size (100) so this call
    // continues to render "all VAs at once" for businesses under the cap.
    // A follow-up will add a load-more UI + true multi-page support.
    const response = await axios.get<
      AdminPaginatedEnvelope<BusinessPaymentMethod[]>
    >(`/v2/admin/business/${businessId}/virtual-accounts?page=0&limit=100`, {
      signal,
    });
    return response.data?.data ?? [];
  },
};
